import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOMeta from '@/components/feature/SEOMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { videos } from '@/mocks/videos';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const videoId = parseInt(id || '1', 10);
  const video = videos.find((v) => v.id === videoId);
  const relatedArticle = articles.find((a) => a.category === video?.category);

  if (!video) {
    return (
      <>
        <SEOMeta
          title="页面未找到 - 草莓视频在线播放"
          description="您访问的页面不存在。草莓视频在线播放平台，提供海量高清视频资源。"
        />
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-secondary-100">
              <i className="ri-error-warning-line text-2xl text-secondary-500"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-900 mb-2">页面未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">该视频资源不存在或已下架</p>
            <Link
              to="/"
              className="inline-block px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: video.category === 'movie' ? '电影' : video.category === 'tv' ? '电视剧' : video.category === 'anime' ? '动漫' : video.category === 'variety' ? '综艺' : video.category === 'documentary' ? '纪录片' : video.category === 'music' ? '音乐' : video.category === 'game' ? '游戏' : video.category === 'sports' ? '体育' : video.category === 'kids' ? '少儿' : '视频', to: `/category/${video.category}` },
    { label: video.title },
  ];

  const categoryName = (() => {
    const map: Record<string, string> = { movie: '电影', tv: '电视剧', anime: '动漫', variety: '综艺', documentary: '纪录片', music: '音乐', game: '游戏', sports: '体育', kids: '少儿' };
    return map[video.category] || '视频';
  })();

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: video.title,
    description: video.description,
    image: video.thumbnail,
    datePublished: `${video.year}-01-15`,
    author: { '@type': 'Organization', name: '草莓视频在线播放' },
  };

  const relatedVideos = videos.filter((v) => v.category === video.category && v.id !== video.id).slice(0, 3);

  return (
    <>
      <SEOMeta
        title={`${video.title} - 草莓视频在线播放 | ${categoryName}高清观看`}
        description={`${video.title}：${video.description} 草莓视频在线播放提供${categoryName}类高清视频资源，无需下载即可流畅观看。草莓视频在线播放,草莓视频高清在线播放,草莓视频免费在线观看。`}
        keywords={`草莓视频在线播放,${video.title},${categoryName}在线观看,草莓视频高清在线播放,草莓视频免费在线观看`}
        canonicalUrl={`https://www.caomei-sp.com/article/${video.id}`}
        ogTitle={`${video.title} - 草莓视频在线播放`}
        ogDescription={video.description}
        ogType="article"
        ogUrl={`https://www.caomei-sp.com/article/${video.id}`}
        jsonLd={articleSchema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-4">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        {/* Video Player Area */}
        <section className="w-full px-4 md:px-6">
          <div className="relative aspect-video bg-foreground-950 rounded-lg overflow-hidden max-w-5xl mx-auto">
            <img
              src={video.thumbnail}
              alt={`${video.title} - 草莓视频在线播放`}
              className="w-full h-full object-cover"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>

            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50 hover:bg-primary-500 hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg">
                <i className="ri-play-fill text-3xl md:text-4xl"></i>
              </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="px-2.5 py-1 bg-background-50/20 backdrop-blur-sm text-background-50 text-xs rounded whitespace-nowrap">
                  {video.quality}
                </span>
                <span className="text-background-200 text-xs">{video.duration}</span>
              </div>
              <div className="flex items-center gap-2">
                {['480P', '720P', '1080P', '4K'].map((q) => (
                  <button
                    key={q}
                    className={`px-2.5 py-1 text-xs rounded whitespace-nowrap cursor-pointer transition-colors ${
                      q === video.quality
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50/20 backdrop-blur-sm text-background-200 hover:bg-background-50/30'
                    }`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Video Info */}
        <section className="w-full px-4 md:px-6 py-6 max-w-5xl mx-auto">
          <h1 className="text-xl md:text-2xl font-heading text-foreground-900 mb-3">
            {video.title}
          </h1>

          <div className="flex items-center gap-4 flex-wrap mb-4">
            <div className="flex items-center gap-1 text-accent-500">
              <i className="ri-star-fill"></i>
              <span className="font-semibold text-sm">{video.rating}</span>
            </div>
            <span className="text-sm text-foreground-500">{video.views}次播放</span>
            <span className="text-sm text-foreground-500">{video.year}</span>
            <span className="text-sm text-foreground-500">{video.updateInfo}</span>
          </div>

          <div className="flex items-center gap-2 flex-wrap mb-6">
            {video.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>

          <p className="text-sm text-foreground-600 leading-relaxed bg-background-100 rounded-lg p-4">
            {video.description}
          </p>
        </section>

        {/* Related Article */}
        {relatedArticle && (
          <section className="w-full px-4 md:px-6 py-8 max-w-5xl mx-auto">
            <h2 className="text-lg font-heading text-foreground-900 mb-4">相关推荐阅读</h2>
            <Link
              to={`/article/${relatedArticle.id}`}
              className="block bg-background-100 rounded-lg p-5 hover:bg-background-200 transition-colors"
            >
              <h3 className="font-medium text-foreground-900 mb-2">{relatedArticle.title}</h3>
              <p className="text-sm text-foreground-500 line-clamp-2">{relatedArticle.summary}</p>
              <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
                <span>{relatedArticle.author}</span>
                <span>{relatedArticle.publishDate}</span>
                <span>{relatedArticle.readTime}阅读</span>
              </div>
            </Link>
          </section>
        )}

        {/* Related Videos */}
        {relatedVideos.length > 0 && (
          <section className="w-full px-4 md:px-6 py-8 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-lg font-heading text-foreground-900 mb-4">同类型推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop="">
                {relatedVideos.map((v) => (
                  <Link
                    key={v.id}
                    to={`/article/${v.id}`}
                    className="block rounded-lg overflow-hidden bg-background-50 hover:bg-background-100 transition-all duration-300"
                    data-product-shop=""
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={v.thumbnail}
                        alt={v.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 bg-black/70 text-background-50 text-xs rounded whitespace-nowrap">
                          {v.quality}
                        </span>
                      </div>
                      <div className="absolute bottom-2 right-2">
                        <span className="px-2 py-0.5 bg-black/70 text-background-50 text-xs rounded whitespace-nowrap">
                          {v.duration}
                        </span>
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-medium text-foreground-900 line-clamp-2">{v.title}</h3>
                      <div className="flex items-center gap-2 mt-2 text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-star-fill text-accent-500 text-xs"></i>
                          {v.rating}
                        </span>
                        <span>{v.views}次播放</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}